import React, { useEffect, useRef, useState } from 'react';
import { Container, Typography, Paper, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { PublicClientApplication } from '@azure/msal-browser';
import participantsApi from '../../../services/api/participants/participantsApi';
import WbAlert from '../../../components/common/WbAlert';
import WbLoader from '../../../components/common/WbLoader';
import paths from '../../../routes/paths';

const msalConfig = {
  auth: {
    clientId: "d7d6a9c6-d51a-49e1-bc34-7d3572b38bfb",
    authority: "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin + "/microsoft-login",
    navigateToLoginRequestUrl: false,
    postLogoutRedirectUri: window.location.origin + "/login"
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  }
};

const loginRequest = {
  scopes: ["User.Read", "profile", "openid", "email"],
  prompt: "select_account"
};

const ExternalRedirectLoginHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const googlePrompted = useRef(false);
  const msalAppRef = useRef(null);

  useEffect(() => {
    if (!msalAppRef.current) {
      msalAppRef.current = new PublicClientApplication(msalConfig);
    }
  }, []);

  useEffect(() => {
    const handleLogin = async () => {
      const pupil = localStorage.getItem('pupil');
      if (pupil) {
        window.location.replace(paths.pupilSurveys || '/surveys');
        return;
      }

      const params = new URLSearchParams(location.search);
      const provider = params.get('provider');
      const redirectAfterLogin = paths.pupilSurveys || '/surveys';
      localStorage.setItem('auth_redirect_url', redirectAfterLogin);

      if (provider === 'microsoft' || window.location.pathname === '/microsoft-login') {
        try {
          const msalApp = msalAppRef.current;
          await msalApp.initialize();
          setLoadingMessage('Processing Microsoft login...');

          if (window.location.pathname === '/microsoft-login') {
            try {
              const response = await msalApp.handleRedirectPromise();
              if (response) {
                await handleMicrosoftResponse(response);
                return;
              }

              const hash = window.location.hash.substring(1);
              const searchParams = new URLSearchParams(window.location.search);
              const code = searchParams.get('code') || (hash ? new URLSearchParams(hash).get('code') : null);

              if (code) {
                try {
                  const tokenResponse = await msalApp.acquireTokenByCode({
                    code,
                    scopes: loginRequest.scopes,
                    redirectUri: msalConfig.auth.redirectUri
                  });
                  await handleMicrosoftResponse(tokenResponse);
                  return;
                } catch (tokenError) {
                  window.location.replace(paths.pupilDashLogin);
                  return;
                }
              }

              window.location.replace(paths.pupilDashLogin);
              return;
            } catch (redirectError) {
              window.location.replace(paths.pupilDashLogin);
              return;
            }
          }

          try {
            await msalApp.loginRedirect(loginRequest);
          } catch (loginError) {
            setError('Failed to start Microsoft login. Please try again.');
            WbAlert({message: 'Failed to start Microsoft login. Please try again.', type: 'error'});
          }
        } catch (e) {
          setError('Microsoft login failed. ' + (e?.message || ''));
          WbAlert({message: 'Microsoft login failed. ' + (e?.message || ''), type: 'error'});
        }
        return;
      }

      if (provider === 'google' && !googlePrompted.current) {
        googlePrompted.current = true;
        setLoadingMessage('Redirecting to Google for secure login...');
        const existingIframe = document.querySelector('iframe[title="Sign in with Google"]');
        if (existingIframe) existingIframe.remove();
        
        const startGoogleOneTap = () => {
          try {
            window.google.accounts.id.initialize({
              client_id: require('../../../config/auth').authConfig.google.clientId,
              callback: handleGoogleCredentialResponse,
              auto_select: true,
              ux_mode: 'redirect',
              login_uri: window.location.origin + '/auth/external-login?provider=google',
            });
            window.google.accounts.id.prompt();
          } catch (e) {
            setError('Google login failed to initialize. ' + (e?.message || ''));
          }
        };

        if (!window.google || !window.google.accounts || !window.google.accounts.id) {
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = startGoogleOneTap;
          script.onerror = () => {
            setError('Failed to load Google login. Please check your network or try again.');
          };
          document.body.appendChild(script);
        } else {
          startGoogleOneTap();
        }
      }
    };

    handleLogin();
  }, [location]);

  const handleMicrosoftResponse = async (response) => {
    if (!response || !response.account) {
      setError('Failed to get user information from Microsoft.');
      WbAlert({message: 'Failed to get user information from Microsoft.', type: 'error'});
      return;
    }

    try {
      const email = response.account.username;
      const payload = { email, idToken: response.idToken || '', provider: 'microsoft' };
      const apiResponse = await participantsApi.verifyStudent(payload);
      let student = typeof apiResponse === 'string' ? JSON.parse(apiResponse) : apiResponse;

      if (student && student.studentId > 0) {
        const studentWithProvider = { ...student, provider: 'microsoft' };
        localStorage.setItem('pupil', JSON.stringify(studentWithProvider));
        const { loginSuccess } = require('../../../actions/authActions');
        const { store } = require('../../../redux/store');
        store.dispatch(loginSuccess({ user: studentWithProvider, provider: 'microsoft' }));
        WbAlert({message: 'Login successful!', type: 'success'});
        
        const redirectUrl = localStorage.getItem('auth_redirect_url') || paths.pupilSurveys;
        localStorage.removeItem('auth_redirect_url');
        
        setTimeout(() => {
          window.location.replace(redirectUrl);
        }, 100);
      } else {
        setError('No student account found with this email. Please contact your school administrator.');
        WbAlert({ message: 'No student account found with this email. Please contact your school administrator.', type: 'error' });
      }
    } catch (error) {
      setError(error.message || 'Something went wrong. Please try again.');
      WbAlert({ message: error.message || 'Something went wrong. Please try again.', type: 'error' });
    }
  };

  const handleGoogleCredentialResponse = async (credentialResponse) => {
    try {
      if (!credentialResponse || !credentialResponse.credential) {
        setError('Google login failed. No credential received.');
        WbAlert({ message: 'Google login failed. No credential received.', type: 'error' });
        return;
      }

      if (credentialResponse.error) {
        setError('Google login failed: ' + credentialResponse.error);
        WbAlert({ message: 'Google login failed: ' + credentialResponse.error, type: 'error' });
        return;
      }

      const decoded = jwtDecode(credentialResponse.credential);
      const { email } = decoded;
      const payload = { email, idToken: credentialResponse.credential, provider: 'google' };
      const response = await participantsApi.verifyStudent(payload);
      let student = typeof response === 'string' ? JSON.parse(response) : response;

      if (student && student.studentId > 0) {
        const studentWithProvider = { ...student, provider: 'google' };
        localStorage.setItem('pupil', JSON.stringify(studentWithProvider));
        const { loginSuccess } = require('../../../actions/authActions');
        const { store } = require('../../../redux/store');
        store.dispatch(loginSuccess({ user: studentWithProvider, provider: 'google' }));
        WbAlert({message: 'Login successful!', type: 'success'});
        
        const redirectUrl = localStorage.getItem('auth_redirect_url') || paths.pupilSurveys;
        localStorage.removeItem('auth_redirect_url');
        
        setTimeout(() => {
          window.location.replace(redirectUrl);
        }, 100);
      } else {
        setError('No student account found with this email. Please contact your school administrator.');
        WbAlert({ message: 'No student account found with this email. Please contact your school administrator.', type: 'error' });
      }
    } catch (error) {
      setError(error.message || 'Something went wrong. Please try again.');
      WbAlert({ message: error.message || 'Something went wrong. Please try again.', type: 'error' });
    }
  };

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} style={{ padding: 32, textAlign: 'center' }}>
          <Typography variant="h6" color="error">{error}</Typography>
          <Button variant="contained" onClick={() => navigate(paths.pupilDashLogin || '/login', { replace: true })} sx={{ mt: 2 }}>Back to Login</Button>
        </Paper>
      </Container>
    );
  }

  return <WbLoader message={loadingMessage || 'Logging you in...'} />;
};

export default ExternalRedirectLoginHandler;
